import React, {
 useState, useMemo, useCallback, useRef, useEffect
} from 'react';
import { Picker, Emoji, BaseEmoji } from 'emoji-mart';
import { useTranslation } from 'react-i18next';

import Icon, { IconTypes } from '@common/components/icon';
import { combineClassNames } from '@common/utils/combineClassNames';
import { Popover, PopoverTrigger, PopoverContent } from '@common/components/ui/popover';
import { cn } from '@common/lib/utils';

export type EmojiPickerProps = {
  onSelect?: (...args: any[]) => any
  triggerIcon?: string
  emoji?: BaseEmoji;
  className?: string;
  size?: number;
  error?: string | boolean;
};

export const EmojiPicker = ({
  triggerIcon = 'insert_emoticon', emoji, className, onSelect, size = 20, error,
}: EmojiPickerProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const triggerClassName = combineClassNames('EmojiPicker__triggerIcon triggerIcon', className, {
    'EmojiPicker__triggerIcon--invalid': !!error,
  });
  const i18n = useMemo(() => {
    return {
      search: t('core:emoji_search'),
      clear: t('core:emoji_clear'), // Accessible label on "clear" button
      notfound: t('core:emoji_not_found'),
      skintext: t('core:emoji_skin_text'),
      categories: {
        search: t('core:emoji_categories_search'),
        recent: t('core:emoji_categories_recent'),
        smileys: t('core:emoji_categories_smileys'),
        people: t('core:emoji_categories_people'),
        nature: t('core:emoji_categories_nature'),
        foods: t('core:emoji_categories_foods'),
        activity: t('core:emoji_categories_activity'),
        places: t('core:emoji_categories_places'),
        objects: t('core:emoji_categories_objects'),
        symbols: t('core:emoji_categories_symbols'),
        flags: t('core:emoji_categories_flags'),
        custom: t('core:emoji_categories_custom'),
      },
      categorieslabel: t('core:emoji_categories_label'),
      skintones: {
        '1': t('core:emoji_skintone_default'),
        '2': t('core:emoji_skintone_light'),
        '3': t('core:emoji_skintone_medium-light'),
        '4': t('core:emoji_skintone_medium'),
        '5': t('core:emoji_skintone_medium-dark'),
        '6': t('core:emoji_skintone_dark'),
      },
    };
  }, [t]);

  const handleEmojiSelect = useCallback((selectedEmoji: any) => {
    if (onSelect) {
      onSelect(selectedEmoji);
      setOpen(false);
    }
  }, [onSelect]);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
  }, []);

  const popoverContentRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  // Handle wheel events to enable mouse scrolling
  useEffect(() => {
    if (!open) return;

    let timeoutId: NodeJS.Timeout | null = null;

    const handleWheel = (e: WheelEvent) => {
      if (!scrollContainerRef.current) return;

      const scrollContainer = scrollContainerRef.current;
      const canScrollDown = scrollContainer.scrollHeight > scrollContainer.clientHeight &&
        scrollContainer.scrollTop < scrollContainer.scrollHeight - scrollContainer.clientHeight;
      const canScrollUp = scrollContainer.scrollTop > 0;

      if ((e.deltaY > 0 && canScrollDown) || (e.deltaY < 0 && canScrollUp)) {
        scrollContainer.scrollTop += e.deltaY;
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Wait for emoji-mart to render, then attach wheel handler
    const findAndAttachScrollHandler = () => {
      // Use ref to scope the query to this component's popover
      const scrollContainer = popoverContentRef.current?.querySelector('.emoji-mart-scroll') as HTMLElement;
      if (!scrollContainer) {
        // Retry after a short delay if not found yet
        timeoutId = setTimeout(findAndAttachScrollHandler, 50);
        return;
      }

      scrollContainerRef.current = scrollContainer;
      scrollContainer.addEventListener('wheel', handleWheel, { passive: false });
    };

    findAndAttachScrollHandler();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('wheel', handleWheel);
        scrollContainerRef.current = null;
      }
    };
  }, [open]);

  const triggerElement = emoji ? (
    <span className={triggerClassName}>
      <Emoji emoji={emoji.colons || emoji} size={size} />
    </span>
  ) : (
    <Icon
      className={triggerClassName}
      type={triggerIcon as IconTypes}
      size={size}
    />
  );

  if (!onSelect) {
    return triggerElement;
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(`tw-inline-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-none
            tw-cursor-pointer`, triggerClassName)}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {triggerElement}
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref={popoverContentRef}
        className={cn('EmojiPicker__Popup EmojiPicker !tw-p-0 !tw-w-auto !tw-max-h-[400px]', className)}
        align="start"
        side="top"
        sideOffset={4}
        onInteractOutside={(event) => {
          // Prevent closing when clicking on emoji-mart elements
          const target = event.target as HTMLElement;
          if (!target) return;

          // Use composedPath to check if click is within emoji-mart
          const path = event.composedPath ? event.composedPath() : [target];
          const isWithinEmojiMart = path.some((element) => {
            if (!(element instanceof HTMLElement)) return false;
            return element.classList?.contains('emoji-mart');
          });

          if (isWithinEmojiMart) {
            event.preventDefault();
          }
        }}
      >
        <div className="tw-relative">
          <Picker i18n={i18n} showPreview={false} onSelect={handleEmojiSelect} />
          <div className="EmojiPicker__Popup__Close">
            <Icon type="close" onClick={() => setOpen(false)} />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
